<template>
  <el-dialog
    :title="isEdit ? $t('edit_service') : $t('add_new_service')"
    v-model="visible"
    width="500px"
    @closed="resetForm"
  >
    <div v-if="isEdit" class="text-muted small mb-4">{{ $t('changes_apply_to_new_orders') }}</div>

    <form @submit.prevent="save">
      <!-- Icon Upload -->
      <div class="form-group mb-4">
        <label class="d-block">{{ $t('service_icon') }}</label>
        <div class="border border-dashed rounded p-4 text-center cursor-pointer" @click="$refs.fileInput.click()">
          <div v-if="previewUrl || form.icon_url">
            <img :src="previewUrl || form.icon_url" width="60" height="60" class="mb-2 rounded" />
            <div class="text-muted small">{{ $t('click_to_change') }}</div>
          </div>
          <div v-else>
            <i class="el-icon-upload text-muted" style="font-size: 24px"></i>
            <div class="text-muted mt-2">{{ $t('drag_drop_or_click') }}<br/><small>SVG or PNG</small></div>
          </div>
          <input type="file" ref="fileInput" class="d-none" accept=".png,.svg,.jpg,.jpeg" @change="onFileChange" />
        </div>

        <div class="d-flex justify-content-center mt-3" style="gap:10px">
          <i v-for="icon in presetIcons" :key="icon" :class="'el-icon-' + icon" class="text-muted cursor-pointer" style="font-size:20px" @click="selectPreset(icon)"></i>
        </div>
      </div>

      <div class="form-group">
        <label>{{ $t('service_name_ar') }} <span class="text-danger">*</span></label>
        <el-input v-model="form.name_ar" :placeholder="$t('eg_express_delivery')" required></el-input>
      </div>

      <div class="form-group">
        <label>{{ $t('service_name_en') }} <span class="text-danger">*</span></label>
        <el-input v-model="form.name_en" placeholder="e.g. Express Delivery" style="direction: ltr" required></el-input>
      </div>

      <div class="form-group">
        <label>{{ $t('slug') }} <small class="text-muted">— {{ $t('auto_generated') }}</small></label>
        <el-input v-model="form.slug" placeholder="/services/">
          <template #prepend>/services/</template>
        </el-input>
      </div>

      <div class="form-group">
        <label>{{ $t('service_description') }} <small class="text-muted">— {{ $t('optional') }}</small></label>
        <el-input type="textarea" v-model="form.description" :placeholder="$t('short_description_for_providers_and_customers')"></el-input>
      </div>

      <div class="form-group">
        <label>{{ $t('fixed_price') }} <span class="text-danger">*</span></label>
        <el-input v-model="form.fixed_price" type="number" step="0.01" required>
          <template #prepend>{{ $t('currency') }}</template>
        </el-input>
        <small class="text-muted d-block mt-1">{{ $t('added_automatically_to_order_total') }}</small>
      </div>

      <div class="form-group">
        <label>{{ $t('target_audience') }} <span class="text-danger">*</span></label>
        <div class="border rounded p-3 mb-2 d-flex justify-content-between align-items-center">
          <div><i class="el-icon-user text-primary mr-2"></i> {{ $t('individuals_orders') }}</div>
          <el-checkbox v-model="form.available_for_individuals"></el-checkbox>
        </div>
        <div class="border rounded p-3 d-flex justify-content-between align-items-center">
          <div><i class="el-icon-s-shop text-warning mr-2"></i> {{ $t('stores_orders') }}</div>
          <el-checkbox v-model="form.available_for_merchants"></el-checkbox>
        </div>
        <small class="text-danger mt-1 d-block" v-if="!form.available_for_individuals && !form.available_for_merchants">
          {{ $t('must_select_one_audience') }}
        </small>
      </div>

      <div class="form-group">
        <div class="border rounded p-3 d-flex justify-content-between align-items-center bg-light">
          <div>
            <strong>{{ $t('enable_for_providers') }}</strong>
            <div class="text-muted small">{{ $t('will_appear_immediately') }}</div>
          </div>
          <el-switch v-model="form.is_active" active-color="#13ce66"></el-switch>
        </div>
      </div>

      <div v-if="isEdit" class="alert alert-warning d-flex align-items-start mt-4">
        <i class="el-icon-warning mt-1 mr-2"></i>
        <small>{{ $t('price_change_warning') }}</small>
      </div>

    </form>
    <template #footer>
      <span class="dialog-footer d-flex justify-content-between">
        <el-button @click="visible = false">{{ $t('cancel') }}</el-button>
        <el-button type="primary" @click="save" :loading="saving" :disabled="!isFormValid">
          {{ isEdit ? $t('save_changes') : $t('add_service') }}
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script>
import { createService, updateService } from '@/api/services';
import mixin from "@/mixins/common";

export default {
  name: 'ServicesFormModal',
  data() {
    return {
      visible: false,
      saving: false,
      isEdit: false,
      previewUrl: null,
      selectedFile: null,
      presetIcons: ['box', 'shopping-cart-2', 'bicycle', 'truck', 'setting'],
      form: this.getDefaultForm()
    };
  },
  mixins: [mixin],
  computed: {
    isFormValid() {
      return this.form.name_ar && this.form.name_en && this.form.fixed_price !== '' && (this.form.available_for_individuals || this.form.available_for_merchants);
    }
  },
  methods: {
    getDefaultForm() {
      return {
        id: null,
        name_ar: '',
        name_en: '',
        slug: '',
        description: '',
        fixed_price: 0,
        available_for_individuals: false,
        available_for_merchants: false,
        is_active: true,
        icon_url: null
      };
    },
    open(service = null) {
      if (service) {
        this.isEdit = true;
        this.form = { ...service };
      } else {
        this.isEdit = false;
        this.form = this.getDefaultForm();
      }
      this.visible = true;
    },
    resetForm() {
      this.form = this.getDefaultForm();
      this.selectedFile = null;
      this.previewUrl = null;
      this.isEdit = false;
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.selectedFile = file;
      this.previewUrl = URL.createObjectURL(file);
    },
    selectPreset(icon) {
      // For a real implementation we might just load a static asset,
      // here we just visually pretend or set icon class if backend supported it.
    },
    async save() {
      if (!this.isFormValid) return;

      this.saving = true;
      try {
        const formData = new FormData();
        formData.append('name_ar', this.form.name_ar);
        formData.append('name_en', this.form.name_en);
        formData.append('slug', this.form.slug || '');
        formData.append('description', this.form.description || '');
        formData.append('fixed_price', this.form.fixed_price);
        formData.append('available_for_individuals', this.form.available_for_individuals ? 1 : 0);
        formData.append('available_for_merchants', this.form.available_for_merchants ? 1 : 0);
        formData.append('is_active', this.form.is_active ? 1 : 0);

        if (this.selectedFile) {
          formData.append('icon', this.selectedFile);
        }

        if (this.isEdit) {
          await updateService(this.form.id, formData);
          this.runToast(this.$t('service_updated'),'success');
        } else {
          await createService(formData);
          this.runToast(this.$t('service_added'),'success');
        }

        this.$emit('saved');
        this.visible = false;
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 422) {
            this.runToast(error.response.data.message || this.$t('validation_error'),'error');
        } else {
            this.runToast(this.$t('error_saving_data'),'error');
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.border-dashed {
  border-style: dashed !important;
  border-color: #dcdfe6;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
